import React, { FC, useEffect, useState, useMemo } from "react";
import { ClientAutoComplete, ClientValue } from "@design-system";
import { clientPresentInSearch, useFilteredClientsLazy } from "../../hooks/clients";
import { createClient, createClientVariables } from "../../graphql/types";
import { FILTERED_CLIENTS_QUERY } from "../../graphql/query";
import { CREATE_CLIENT } from "../../graphql/mutation";
import { useMutation } from "../../hooks";

interface Props {
  value?: number[];
  onChange?: (value: number[]) => void;
  requiredClients?: ClientValue[];
  canCreateClient?: boolean;
}

export const MultiSelectClientContainer: FC<Props> = ({
  value = [],
  onChange,
  requiredClients: propsRequiredClients = [],
  canCreateClient = true,
}) => {
  const [requiredClients, setRequiredClients] = useState(propsRequiredClients);

  const displayValue = useMemo(() => {
    return value
      .map((id: any) => requiredClients.find((client: any) => client.id === id))
      .filter((client): client is ClientValue => client !== undefined);
  }, [value, requiredClients]);

  const clientFilterData = useFilteredClientsLazy(10);

  const [createClient] = useMutation<createClient, createClientVariables>(CREATE_CLIENT, {
    update: (cache, mutationResult) => {
      const newClient = mutationResult.data?.createClient;
      const data: any = cache.readQuery({
        query: FILTERED_CLIENTS_QUERY,
        variables: clientFilterData?.queryVars,
      });
      if (data) {
        const clientNameSearch = clientFilterData?.queryVars!.filter!.search![0] || "";
        const clientInSearch = clientPresentInSearch(clientNameSearch, newClient);
        if (clientInSearch) {
          cache.writeQuery({
            query: FILTERED_CLIENTS_QUERY,
            variables: clientFilterData?.queryVars,
            data: {
              filteredClients: {
                clients: [...data.filteredClients.clients, newClient],
                total: data.filteredClients.total + 1,
              },
            },
          });
        }
      }
    },
  });

  return (
    <ClientAutoComplete
      onChangeMultipleClients={(clients) => {
        const newValue = clients.map((client: any) => client.id);
        const newRequiredClients = [...requiredClients, ...clients];
        setRequiredClients(newRequiredClients);
        onChange && onChange(newValue || []);
      }}
      clientFilterData={{
        ...clientFilterData,
        clients: [...clientFilterData.clients, ...requiredClients],
      }}
      onClientSave={(clientData) => {
        createClient({ variables: { data: clientData as any } });
      }}
      bordered
      value={displayValue}
      defaultValue={displayValue}
      mode="multiple"
      canCreateClient={canCreateClient}
    />
  );
};
